<template>
  <form
    role="search"
    aria-label="Search"
  >
    <v-menu
      v-model="expanded"
      :content-class="searchContainerClass"
      offset-y
      :close-on-content-click="false"
      bottom
      allow-overflow
      nudge-bottom="6"
    >
      <template v-slot:activator="{ on: searchContainerOn }">
        <div v-on="searchContainerOn">
          <v-text-field
            v-model="searchText"
            autocomplete="off"
            solo
            flat
            :label="searchLabel"
            hide-details
            prepend-inner-icon="search"
            :loading="isLoadingSearch"
            :placeholder="searchText"
            :search-input.sync="searchText"
            @keydown.up="onArrowUp()"
            @keydown.down="onArrowDown()"
            @input="searchItems(searchText)"
          >
            <template v-slot:append>
              <v-col>
                <v-row class="flex-nowrap">
                  <v-btn
                    icon
                    :disabled="disableFilterButton"
                    @click.stop="editCurrentReport"
                  >
                    <v-icon>
                      filter_list
                    </v-icon>
                  </v-btn>

                  <v-dialog
                    v-model="showModalShare"
                    max-width="500"
                  >
                    <template v-slot:activator="{ on }">
                      <v-btn
                        :disabled="disableFilterButton || !selectedReport || !selectedReport.canShareSoft"
                        icon
                        v-on="on"
                        @click.stop="toggleModal('Share')"
                      >
                        <v-icon
                          icon
                        >
                          output
                        </v-icon>
                      </v-btn>
                    </template>

                    <share-form />
                  </v-dialog>

                  <bookmark-icon />
                </v-row>
              </v-col>
            </template>

            <template v-slot:label>
              <span>
                {{ searchLabel }}
              </span>
            </template>
          </v-text-field>
        </div>
      </template>

      <v-card class="search-results">
        <v-container
          v-if="showSearchPreferences"
          class="pa-0"
          fluid
        >
          <v-card-title class="justify-space-between px-4 pt-2 pb-0">
            <span class="subtitle-2">Search Preferences</span>

            <v-btn
              :ripple="false"
              icon
              title="Close"
              @click.stop="showSearchPreferences = false"
            >
              <v-icon
                :color="highlightColor"
                icon="close"
                small
              >
                close
              </v-icon>
            </v-btn>
          </v-card-title>
        </v-container>

        <v-container
          v-else
          class="pa-0 ma-0"
          fluid
        >
          <v-card-title class="justify-space-between px-4 pt-2 pb-0">
            <span class="subtitle-2">Search</span>

            <v-btn
              :ripple="false"
              icon
              title="Search Preferences"
              @click.stop="showSearchPreferences = true"
            >
              <v-icon
                class="material-icons-outlined"
                :color="highlightColor"
                icon="close"
                small
              >
                settings
              </v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text
            v-for="endpoint in searchEndpoints"
            :key="endpoint.baseReportName"
            class="pa-1"
          >
            <v-list
              ref="search-list"
              subheader
              class="py-0 very-dense search-results-list"
            >
              <v-subheader :class="`search-header ${titleColor}--text px-3`">
                <div>
                  <router-link :to="endpoint.href">
                    {{ endpoint.name }}
                  </router-link>
                </div>

                <v-spacer />

                <div v-if="endpoint.error">
                  {{ endpoint.error.code }} Error
                </div>

                <div
                  v-else-if="searchText || (endpoint.results && endpoint.results.length)"
                  :class="`${endpoint.results.length ? 'cursor-pointer font-weight-bold primary--text' : `${textColor}--text`}`"
                  @click="toggleAllResults(endpoint.reportKey)"
                >
                  {{ endpoint.results.length }} {{ endpoint.results.length === 1 ? 'Result' : 'Results' }}
                </div>
              </v-subheader>

              <v-list-item
                v-for="(item, index) in mySearchResults[endpoint.reportKey]"
                :key="`${endpoint.reportKey}-${index}`"
                :aria-label="item.text"
                @click="selectSearchItem(item)"
              >
                <v-list-item-content
                  class="search-result pa-0"
                >
                  <v-list-item-title
                    class="body-2 pa-0"
                  >
                    <span
                      v-for="(chunk, i) of item.chunks"
                      :key="i"
                      :class="chunk.isMatched ? 'font-weight-bold' : 'font-weight-medium'"
                      :data-chunk-index="chunk.position"
                    >{{ chunk.value }}</span>

                    <span v-if="!!item.chunks === false">{{ item.text }}</span>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card-text>

          <v-divider class="mt-3" />

          <v-card-text class="pa-0">
            <v-list>
              <v-list-item>
                <v-list-item-content
                  class="search-result pa-0"
                >
                  <v-list-item-title
                    class="subtitle-2 font-weight-medium pa-1"
                  >
                    <span>
                      <router-link to="/docs">User Docs</router-link>
                    </span>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-container>
      </v-card>
    </v-menu>

    <span
      v-if="didLoadSearchFail && searchText"
      class="error-message"
    >
      <v-icon>error_outline</v-icon>
      Failed to search. Please try again later.
    </span>
  </form>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

import render from 'src/utils/render'
import { copyObject } from '@/utils/objects'

const { mapGetters: mapAuthGetters, mapState: mapAuthState } = createNamespacedHelpers('auth')
const { mapGetters: mapLayoutGetters } = createNamespacedHelpers('layout')
const { mapState: mapReportState } = createNamespacedHelpers('report')
const { mapGetters: mapToolsGetters, mapState: mapToolsState } = createNamespacedHelpers('tools')

export default {
  name: 'SearchBar',
  components: {
    BookmarkIcon: () => import('src/bookmark/views/BookmarkIcon'),
    ShareForm: () => import('src/layout/components/ShareForm'),
  },
  data: () => ({
    searchLabelDefault: 'Search ...',
    searchLabelFailed: 'Try another search ...',
    searchResults: { endpoints: {}, total: {} },
    searchText: '',
    expanded: false,
    ready: false,
    seeAllResults: false,
    showSearchPreferences: false,

    copied: false,

  }),
  computed: {
    ...mapAuthGetters([
      'inDarkMode',
      'inDemoMode',
      'isInternalUser',
      'maxSearchResults',
    ]),
    ...mapAuthState({
      user: (state) => state.user,
    }),
    ...mapLayoutGetters([
      'iceColor',
      'midColor',
      'textColor',
      'titleColor',
    ]),
    ...mapReportState({
      $isCreatingReport: (state) => state.isCreatingReport,
      $isSubmittingReportUpdate: (state) => state.isSubmittingReportUpdate,
      $roleOptions: (state) => state.roleOptions,
      $selectedReport: (state) => state.selectedReport,
      $showModalShare: (state) => state.showModalShare,
    }),
    ...mapToolsGetters([
      'reportOptionsForSelectedTeam',
      'selectedSearch',
    ]),
    ...mapToolsState({
      didLoadSearchFail: (state) => state.didLoadSearchFail,
      isLoadingSearch: (state) => state.isLoadingSearch,
      reportPath: (state) => state.reportPath,
      associationId: (state) => state.associationId,
      baseReportName: (state) => state.baseReportName,
      routeName: (state) => state.routeName,
      subId: (state) => state.subId,
    }),

    highlightColor() {
      return this.iceColor
    },
    secondaryColor() {
      return this.midColor
    },

    currentNavLabel() {
      const { title } = this.currentNav || {}
      return title || ''
    },
    disableFilterButton() {
      return this.isLoadingSearch
        || this.didLoadSearchFail
        || !this.selectedReport
        || !this.associationId
        || !this.reportPath
        || (this.associationId === 'all-associations' && this.routeName !== 'internal')
        || ['dashboard', 'docs', 'home', 'time-sheet'].indexOf(this.routeName) !== -1
    },

    roleOptions() {
      return this.$roleOptions
    },

    selectedReport: {
      get() {
        return this.$selectedReport
      },
      set(newValue) {
        this.$store.commit('report/selectedReport', newValue)
      },
    },

    showModalShare: {
      get() {
        return this.$showModalShare
      },
      set(value) {
        this.$store.commit('report/showModalShare', value)
      },
    },

    rulesRequired() {
      return (query) => {
        const { label, baseReportName } = query
        return [
          (value) => {
            const isStillEditing = !label && !baseReportName
            const isValidValue = isStillEditing || !!value || [0, false].indexOf(value) !== -1
            return isValidValue || 'Required'
          },
        ]
      }
    },

    mySearchResults() {
      const { searchResults: { endpoints: allowedEndpoints } } = this
      const endpointNames = Object.keys(allowedEndpoints)

      return endpointNames.reduce((endpoints, endpointName) => {
        const allowedEndpoint = allowedEndpoints[endpointName]
        endpoints[endpointName] = this.getSearchResults(allowedEndpoint)
        return endpoints
      }, {})
    },
    searchEndpoints() {
      const allEndpoints = this.searchResults.endpoints
      const getEndpointsWithResults = () => Object.keys(allEndpoints).reduce((newEndpoints, endpointKey) => {
        const endpoint = allEndpoints[endpointKey]
        const hasResults = endpoint.results && endpoint.results.length
        if (hasResults) {
          endpoint.reportKey = endpoint.baseReportName || endpoint.routeName
          newEndpoints[endpointKey] = endpoint
        }
        return newEndpoints
      }, {})
      return (this.searchText && getEndpointsWithResults()) || allEndpoints
    },
    searchTotal() {
      return this.searchResults.total
    },
    searchLabel() {
      const {
        didLoadSearchFail,
        isLoadingSearch,
        selectedSearch,
      } = this

      if (!selectedSearch) return this.searchLabelDefault

      const { textAlt } = selectedSearch

      if (isLoadingSearch) return `Loading ${textAlt} ...`

      if (didLoadSearchFail) return this.searchLabelFailed

      return textAlt
    },
    showAllResults: {
      get() {
        const { seeAllResults } = this

        if (seeAllResults) return seeAllResults

        const { isInternalUser, searchEndpoints } = this
        const searchEndpointNames = Object.keys(searchEndpoints)

        return searchEndpointNames.reduce((endpoints, endpointName) => {
          const show = !isInternalUser && ['discrepancy', 'yield', 'pacing'].includes(endpointName)

          endpoints[endpointName] = show

          return endpoints
        }, {})
      },
      set(newValue) {
        const { showAllResults } = this
        this.seeAllResults = { ...showAllResults, [newValue]: !showAllResults[newValue] }
      },
    },
    showResults() {
      const {
        didLoadSearchFail,
        isLoadingSearch,
        searchTotal,
        searchText,
      } = this
      const loading = isLoadingSearch === false
      const failed = didLoadSearchFail === false
      const results = searchTotal?.results !== 0
      const haveSearchText = searchText.length > 0

      return loading && failed && haveSearchText && results
    },
    searchContainerClass() {
      const { menuClass, searchTotal, searchText } = this
      const results = searchTotal?.results > 0
      const recentSearches = searchTotal?.recent > 0
      const haveContent = results || recentSearches || searchText

      const baseClasses = 'search-results-container white'
      const menuClasses = menuClass ? ` ${menuClass}` : ''
      const resultClasses = haveContent ? '' : ' results-empty'
      const containerClass = `${baseClasses}${menuClasses}${resultClasses}`

      return containerClass
    },
  },
  watch: {
    didLoadSearchFail(newValue) {
      if (newValue) {
        this.expanded = false
      }
    },
    inDemoMode() {
      this.setup()
    },
    $route: {
      handler() {
        const { bookmark } = this.$route.query
        if (bookmark) this.editBookmarksList()
      },
      immediate: true,
    },
    reportOptionsForSelectedTeam: {
      deep: true,
      handler(newValue) {
        const { reportPath = 'all-data' } = this.$route.query
        const selectedReport = newValue && newValue.find((report) => report.reportPath === reportPath || report.docId === reportPath)

        if (selectedReport) {
          const { selectedReport: lastSelectedReport } = this
          const hasChangedReport = () => {
            const hasChangedDocId = () => {
              const hasLastAndCurrentSelectedReport = lastSelectedReport && selectedReport
              const hasDocIdOnParentOfLast = !!lastSelectedReport && lastSelectedReport.docIdOnParent
              const hasDocIdOnParentOfCurrent = !!selectedReport && selectedReport.docIdOnParent
              return hasLastAndCurrentSelectedReport && (
                (hasDocIdOnParentOfLast && hasDocIdOnParentOfCurrent && lastSelectedReport.docIdOnParent !== selectedReport.docIdOnParent)
                || ((!hasDocIdOnParentOfCurrent || !hasDocIdOnParentOfLast) && lastSelectedReport.docId !== selectedReport.docId)
              )
            }
            const isSavingReport = () => lastSelectedReport.docId === selectedReport.docId && this.$isSubmittingReportUpdate
            const isCreatingReport = () => {
              if (this.$isCreatingReport) {
                this.$store.commit('report/isCreatingReport', false)
                return true
              }
              return false
            }
            return isCreatingReport() || hasChangedDocId() || isSavingReport()
          }

          if (!lastSelectedReport || !selectedReport || hasChangedReport()) {
            this.selectedReport = copyObject(selectedReport)
          }
        } else if (!reportPath) {
          const defaultReport = newValue.find((report) => report.reportPath === 'all-data')
          this.selectedReport = copyObject(defaultReport)
        }
      },
      immediate: true,
    },
  },
  created() {
    this.setup()
  },
  methods: {
    async setup() {
      const searchTerm = ''

      this.searchText = searchTerm
      this.expanded = false

      await this.$store.dispatch('tools/setupNav')

      this.searchResults = await this.$store.dispatch('tools/setSearchAdvanced', searchTerm)

      this.ready = true
    },

    toggleModal(modalName, modalData) {
      const storeName = 'report'
      this.$store.dispatch('tools/toggleModal', { modalData, modalName, storeName })
    },

    onArrowUp() {
      render.keepHighlightedElementInScrollView.call(this)
    },
    onArrowDown() {
      render.keepHighlightedElementInScrollView.call(this)
    },
    isCurrentSelection(selection) {
      const { reportPath, associationId, baseReportName, subId } = selection
      const {
        reportPath: currentQueryPath,
        associationId: currentAssociationId,
        baseReportName: currentBaseReportName,
        subId: currentSubId,
      } = this
      return currentQueryPath === reportPath
          && currentAssociationId === String(associationId)
          && currentBaseReportName === baseReportName
          && (currentSubId === subId || (!subId && currentSubId === 'all-secondary'))
    },
    selectSearchItem(selection) {
      this.searchText = ''
      this.expanded = false

      if (this.isCurrentSelection(selection)) {
        const endpointKey = selection.baseReportName || selection.routeName
        const message = `<b>Current Report</b><br>${selection.text} ${endpointKey}`

        this.$store.commit('tools/snackbar', true)
        this.$store.commit('tools/snack', { message, status: 'info' })
      } else {
        this.$store.dispatch('tools/selectSearchItem', selection)
      }
    },
    async searchItems(searchTerm) {
      if (this.ready) {
        this.expanded = true
        this.searchResults = await this.$store.dispatch('tools/setSearchAdvanced', searchTerm)
      }
    },
    getSearchResults(endpoint) {
      const { reportKey, results } = endpoint
      const { maxSearchResults, showAllResults } = this

      if (showAllResults[reportKey]) return results

      return results ? results.slice(0, maxSearchResults) : []
    },
    toggleAllResults(endpointName) {
      this.showAllResults = endpointName
    },

    editCurrentReport() {
      const { params, query } = this.$route
      const { action } = query
      const newAction = action === 'read' ? 'edit' : 'read'

      this.$router.push({ params, query: { ...query, action: newAction } })
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~src/styles/variables";

.search-results-container {
  z-index: 200 !important;
  border-radius: 6px;
  box-shadow: 0 2px 6px 3px rgba(165, 177, 185, 0.3);
}

.search-header {
  text-transform: uppercase;
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.26px;
  line-height: 11px;
}

.search-result {
  &:hover {
    color: $color-monochrome-blue;
  }
}

.search-results {
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 3ch);
}

.search-results-list {
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: auto;
  overflow: auto;
}

.recents-list {
  flex-basis: auto;
  flex-grow: 0;
  flex-shrink: 0;
}

::v-deep.v-input__icon i {
  color: $color-monochrome-darkestblue;
}

@media screen and (max-width: $vuetify-md-max-size) {
  .search-results {
    max-height: calc(100vh - 9ch);
  }
}

.v-list-item {
  min-height: 36px;
}

</style>
